import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  useNotificationPreferences,
  ALL_CHANNELS,
  ALL_NOTIFICATION_TYPES,
} from '../../hooks/use-notification-preferences';

export function NotificationsSettingsPage() {
  const { t } = useTranslation('notifications');
  const {
    disabledChannels,
    optedOutTypes,
    loading,
    saving,
    error,
    savedMsg,
    toggleChannel,
    toggleType,
    save,
    enablePush,
    setError,
    setSavedMsg,
  } = useNotificationPreferences();

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>
        {t('prefs.title')}
      </Typography>
      <Typography color="text.secondary" mb={3}>
        {t('prefs.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {savedMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedMsg(null)}>
          {savedMsg}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          {t('prefs.channelsCard.titleShort')}
        </Typography>
        <Stack spacing={1}>
          {ALL_CHANNELS.map((ch) => (
            <FormControlLabel
              key={ch}
              control={
                <Switch checked={!disabledChannels.has(ch)} onChange={() => toggleChannel(ch)} />
              }
              label={t(`prefs.channels.${ch}.label`)}
            />
          ))}
        </Stack>
        <Box mt={2}>
          <Button size="small" variant="outlined" onClick={enablePush}>
            {t('prefs.actions.enablePush')}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          {t('prefs.typesCard.title')}
        </Typography>
        <Stack spacing={1} divider={<Divider flexItem />}>
          {ALL_NOTIFICATION_TYPES.map((ty) => (
            <FormControlLabel
              key={ty}
              control={<Switch checked={!optedOutTypes.has(ty)} onChange={() => toggleType(ty)} />}
              label={t(`prefs.types.${ty}.label`)}
            />
          ))}
        </Stack>
      </Paper>

      <Box textAlign="right">
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? t('prefs.actions.saving') : t('prefs.actions.save')}
        </Button>
      </Box>
    </Box>
  );
}
